import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { WrenchScrewdriverIcon, HeartIcon, UserGroupIcon, AcademicCapIcon, UserIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../utils/api';
import ImageModal from '../../components/ImageModal';

import { getImageUrlWithEnv } from '../../utils/imageUrl';
const TechnicalStaff = () => {
  const [technicalStaff, setTechnicalStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchTechnicalStaff = async () => {
      try {
        setLoading(true);
        const response = await fetch(getApiUrl('/api/technical-staff/'));
        if (!response.ok) {
          throw new Error('Failed to fetch technical staff');
        }
        const data = await response.json();
        setTechnicalStaff(data);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching technical staff:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTechnicalStaff();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-georgian">მონაცემების ჩატვირთვა...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 font-georgian mb-4">შეცდომა: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded font-georgian"
          >
            ხელახლა ცდა
          </button>
        </div>
      </div>
    );
  }

  const medicalStaff = technicalStaff.filter(staff => 
    staff.position.includes("ექიმი") || staff.position === "ფიზიოთერაპევტი" || staff.position === "ექიმის თანაშემწე"
  );

  const technicalStaffMembers = technicalStaff.filter(staff => 
    !staff.position.includes("ექიმი") && staff.position !== "ფიზიოთერაპევტი" && staff.position !== "ექიმის თანაშემწე"
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            ტექნიკური პერსონალი
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            ჩვენი გუნდის პროფესიონალური ტექნიკური პერსონალი და სპეციალისტები
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <WrenchScrewdriverIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">ტექნიკური პერსონალი</h3>
            <p className="text-2xl font-bold text-purple-600">{technicalStaff.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <HeartIcon className="h-8 w-8 text-red-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">სამედიცინო პერსონალი</h3>
            <p className="text-2xl font-bold text-red-600">{medicalStaff.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <UserGroupIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">ტექნიკური სპეციალისტები</h3>
            <p className="text-2xl font-bold text-green-600">{technicalStaffMembers.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <AcademicCapIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">საშუალო გამოცდილება</h3>
            <p className="text-2xl font-bold text-blue-600">{Math.round(technicalStaff.reduce((sum, s) => sum + parseInt(s.experience), 0) / technicalStaff.length)} წელი</p>
          </div>
        </div>

        {/* Medical Staff */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            სამედიცინო პერსონალი
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {medicalStaff.map((staff, index) => (
              <div key={index} className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-105">
                {/* Staff Photo */}
                <div className="relative h-48 bg-gray-200 cursor-pointer" onClick={() => staff.photo_url && setSelectedImage({
                  imageUrl: getImageUrlWithEnv(staff.photo_url),
                  alt: staff.name,
                  title: staff.name,
                  subtitle: `${staff.position} • ${staff.nationality}`
                })}>
                  {staff.photo_url ? (
                    <img
                      src={getImageUrlWithEnv(staff.photo_url)}
                      alt={staff.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-full h-full ${staff.photo_url ? 'hidden' : 'flex'} items-center justify-center bg-gradient-to-br from-red-500 to-red-700`}
                  >
                    <UserIcon className="h-16 w-16 text-white" />
                  </div>
                </div>
                
                {/* Staff Info */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 font-georgian text-lg mb-1">{staff.name}</h3>
                  <p className="text-sm text-red-600 font-georgian mb-2">{staff.position}</p>
                  
                  <div className="space-y-1 text-sm text-gray-600 font-georgian">
                    {staff.age && (
                      <p>ასაკი: {staff.age} წელი</p>
                    )}
                    <p>{staff.nationality}</p>
                    {staff.experience && (
                      <p>გამოცდილება: {staff.experience}</p>
                    )}
                    {staff.specialization && (
                      <p>სპეციალიზაცია: {staff.specialization}</p>
                    )}
                  </div>
                  
                  {staff.qualifications && staff.qualifications.length > 0 && (
                    <div className="mt-3">
                      <h4 className="font-medium text-gray-900 font-georgian mb-1 text-xs">კვალიფიკაციები:</h4>
                      <ul className="space-y-1">
                        {staff.qualifications.slice(0, 2).map((qualification, idx) => (
                          <li key={idx} className="text-xs text-gray-600 font-georgian flex items-center">
                            <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                            {qualification}
                          </li>
                        ))}
                        {staff.qualifications.length > 2 && (
                          <li className="text-xs text-gray-500 font-georgian">
                            +{staff.qualifications.length - 2} სხვა
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                  
                  {staff.description && (
                    <p className="mt-3 text-xs text-gray-500 font-georgian line-clamp-2">{staff.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Staff */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            ტექნიკური სპეციალისტები
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {technicalStaffMembers.map((staff, index) => (
              <div key={index} className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-105">
                {/* Staff Photo */}
                <div className="relative h-48 bg-gray-200 cursor-pointer" onClick={() => staff.photo_url && setSelectedImage({
                  imageUrl: getImageUrlWithEnv(staff.photo_url),
                  alt: staff.name,
                  title: staff.name,
                  subtitle: `${staff.position} • ${staff.nationality}`
                })}>
                  {staff.photo_url ? (
                    <img
                      src={getImageUrlWithEnv(staff.photo_url)}
                      alt={staff.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-full h-full ${staff.photo_url ? 'hidden' : 'flex'} items-center justify-center bg-gradient-to-br from-purple-500 to-purple-700`}
                  >
                    <UserIcon className="h-16 w-16 text-white" />
                  </div>
                </div>
                
                {/* Staff Info */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 font-georgian text-lg mb-1">{staff.name}</h3>
                  <p className="text-sm text-purple-600 font-georgian mb-2">{staff.position}</p>
                  
                  <div className="space-y-1 text-sm text-gray-600 font-georgian">
                    {staff.age && (
                      <p>ასაკი: {staff.age} წელი</p>
                    )}
                    <p>{staff.nationality}</p>
                    {staff.experience && (
                      <p>გამოცდილება: {staff.experience}</p>
                    )}
                    {staff.specialization && (
                      <p>სპეციალიზაცია: {staff.specialization}</p>
                    )}
                  </div>
                  
                  {staff.qualifications && staff.qualifications.length > 0 && (
                    <div className="mt-3">
                      <h4 className="font-medium text-gray-900 font-georgian mb-1 text-xs">კვალიფიკაციები:</h4>
                      <ul className="space-y-1">
                        {staff.qualifications.slice(0, 2).map((qualification, idx) => (
                          <li key={idx} className="text-xs text-gray-600 font-georgian flex items-center">
                            <span className="w-1.5 h-1.5 bg-purple-600 rounded-full mr-2"></span>
                            {qualification}
                          </li>
                        ))}
                        {staff.qualifications.length > 2 && (
                          <li className="text-xs text-gray-500 font-georgian">
                            +{staff.qualifications.length - 2} სხვა
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                  
                  {staff.description && (
                    <p className="mt-3 text-xs text-gray-500 font-georgian line-clamp-2">{staff.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="text-center">
          <Link
            to="/team"
            className="inline-flex items-center bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 font-georgian"
          >
            ← უკან გუნდის გვერდზე
          </Link>
        </div>
      </div>
      
      {/* Image Modal */}
      <ImageModal
        isOpen={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        imageUrl={selectedImage?.imageUrl}
        alt={selectedImage?.alt}
        title={selectedImage?.title}
        subtitle={selectedImage?.subtitle}
      />
    </div>
  );
};

export default TechnicalStaff;
